import PropTypes from "prop-types";

/**
 * RealisticBattery – High-fidelity 3D industrial battery / energy storage component.
 * Features:
 * - Dynamic 5-segment battery level bar responding to live percentage (0–100%)
 * - Live percentage readout prominently displayed above segments
 * - Dynamic color transitions: Vibrant Green (>40%), Amber (20–40%), Flashing Red (<=20%)
 * - Animated charging lightning bolt and pulse glow
 */
const RealisticBattery = ({
  liveValue,
  isOffline,
  config = {},
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const minV = config.minLevel ?? (config.minValue ?? 0);
  const maxV = config.maxLevel ?? (config.maxValue ?? 100);
  const isCharging = config.isCharging ?? true;

  let pct = 75;
  if (liveValue !== null && liveValue !== undefined && !isNaN(Number(liveValue))) {
    const range = maxV - minV;
    pct = range > 0 ? ((Number(liveValue) - minV) / range) * 100 : 0;
  }
  pct = Math.min(100, Math.max(0, pct));

  const litCount = pct <= 0 ? 0 : pct <= 20 ? 1 : pct <= 40 ? 2 : pct <= 60 ? 3 : pct <= 80 ? 4 : 5;
  const isLow = pct <= 20 && pct > 0;
  const isDead = pct <= 0;

  const barColor = isDead
    ? "#64748b"
    : isLow
    ? "#ef4444"
    : pct <= 40
    ? "#f59e0b"
    : "#22c55e";

  const segments = [
    { x: 187, y: 223, w: 72, h: 63 },
    { x: 266, y: 223, w: 72, h: 63 },
    { x: 345, y: 223, w: 72, h: 63 },
    { x: 424, y: 223, w: 72, h: 63 },
    { x: 503, y: 223, w: 72, h: 63 },
  ];

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
      }}
    >
      {/* 3D Realistic Base Image */}
      <img
        src="/scada-realistic/Battery.png"
        alt="SCADA Realistic Battery"
        className="scada-realistic-img"
      />

      {/* Dynamic Animated Overlay (SVG synchronized in 758x459 space) */}
      <svg
        className="scada-realistic-overlay"
        viewBox="0 0 758 459"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="batSegmentGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* Percentage readout removed — was overlapping the "BATTERY" label on the component image */}

        {/* ── 5 Dynamic Battery Segment Blocks ── */}
        <g filter={!isOffline && !isDead ? "url(#batSegmentGlow)" : "none"}>
          {segments.map((seg, idx) => {
            const isLit = idx < litCount && !isOffline && !isDead;
            return (
              <rect
                key={idx}
                x={seg.x}
                y={seg.y}
                width={seg.w}
                height={seg.h}
                rx="4"
                fill={isLit ? barColor : "#1e293b"}
                opacity={isLit ? 0.95 : 0.4}
                style={{
                  transition: "all 0.3s ease",
                  animation: isLit && isLow ? "scada-led-fast-blink 0.9s infinite" : "none",
                }}
              />
            );
          })}
        </g>

        {/* ── Charging Lightning Bolt Animation ── */}
        {isCharging && !isDead && !isOffline && (
          <g filter="url(#batSegmentGlow)">
            <path
              d="M 380 220 L 364 256 L 381 256 L 368 292 L 400 248 L 384 248 Z"
              fill="#ffffff"
              opacity="0.95"
              style={{ animation: "scada-led-blink 1.2s infinite ease-in-out" }}
            />
          </g>
        )}
      </svg>
    </div>
  );
};

RealisticBattery.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  isOffline: PropTypes.bool,
  config: PropTypes.object,
  opacity: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default RealisticBattery;
